import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "anagha_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const fileUrl = (path) => (path ? `${API}/files/${path}` : null);

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const registerGuest = async ({ name, relationship, selfie }) => {
  const fd = new FormData();
  fd.append("name", name);
  fd.append("relationship", relationship);
  fd.append("selfie", selfie);
  const { data } = await api.post("/guests/register", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const getGuest = (id) => api.get(`/guests/${id}`).then((r) => r.data);
export const getGallery = () => api.get("/gallery").then((r) => r.data);
export const getRoles = () => api.get("/roles").then((r) => r.data);
export const getStats = () => api.get("/kingdom/stats").then((r) => r.data);
export const getKingdomInfo = () => api.get("/kingdom/info").then((r) => r.data);

export const adminLogin = (password) =>
  api.post("/admin/login", { password }).then((r) => r.data);
export const adminList = () => api.get("/admin/guests").then((r) => r.data);
export const adminApprove = (id) => api.patch(`/admin/guests/${id}/approve`).then((r) => r.data);
export const adminReject = (id) => api.patch(`/admin/guests/${id}/reject`).then((r) => r.data);
export const adminRegenerate = (id) => api.post(`/admin/guests/${id}/regenerate`).then((r) => r.data);
export const adminDelete = (id) => api.delete(`/admin/guests/${id}`).then((r) => r.data);

// Love letters
export const getLoveLetters = () => api.get("/love-letters").then((r) => r.data);
export const saveLoveLetter = (role, message) => api.put("/admin/love-letters", { role, message }).then((r) => r.data);
export const deleteLoveLetter = (role) => api.delete(`/admin/love-letters/${role}`).then((r) => r.data);

// Music playlist
export const listMusic = () => api.get("/music").then((r) => r.data);
export const uploadMusic = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/music", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};
export const deleteMusic = (id) => api.delete(`/admin/music/${id}`).then((r) => r.data);
export const setMusicSelected = (id, payload) => {
  // Accepts either a boolean (legacy: dance-floor selected) or an object
  // like { for_director, for_dance_floor, selected }.
  const body = typeof payload === "boolean" ? { selected: payload } : payload;
  return api.patch(`/admin/music/${id}`, body).then((r) => r.data);
};

// Memory photos (admin uploads pictures of Anagha for the slideshow)
export const listPhotos = () => api.get("/photos").then((r) => r.data);
export const uploadPhoto = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/photos", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};
export const deletePhoto = (id) => api.delete(`/admin/photos/${id}`).then((r) => r.data);
export const featurePhoto = (id) => api.post(`/admin/photos/${id}/feature`).then((r) => r.data);
export const getFeaturedPhoto = () => api.get("/photos/featured").then((r) => r.data);

export const getWaMessage = () => api.get("/admin/whatsapp/message").then((r) => r.data);
export const saveWaMessage = (message) => api.put("/admin/whatsapp/message", { message }).then((r) => r.data);
export const deleteWaMessage = () => api.delete("/admin/whatsapp/message").then((r) => r.data);
export const listWaContacts = () => api.get("/admin/whatsapp/contacts").then((r) => r.data);
export const addWaContact = (name, phone) => api.post("/admin/whatsapp/contacts", { name, phone }).then((r) => r.data);
export const deleteWaContact = (id) => api.delete(`/admin/whatsapp/contacts/${id}`).then((r) => r.data);

export const getShareMessage = () => api.get("/admin/shares/message").then((r) => r.data);
export const saveShareMessage = (message) => api.put("/admin/shares/message", { message }).then((r) => r.data);
export const createShare = (guestName, files) => {
  const fd = new FormData();
  fd.append("guest_name", guestName);
  files.forEach((f) => fd.append("files", f));
  return api.post("/admin/shares", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const listShares = () => api.get("/admin/shares").then((r) => r.data);
export const deleteShare = (id) => api.delete(`/admin/shares/${id}`).then((r) => r.data);
export const getShare = (id) => api.get(`/shares/${id}`).then((r) => r.data);
export const downloadUrl = (path, name) =>
  `${API}/files/${path}?download=1&name=${encodeURIComponent(name || "photo")}`;

// Event schedule
export const getEventConfig = () => api.get("/event/config").then((r) => r.data);
export const saveEventConfig = (cfg) => api.put("/admin/event/config", cfg).then((r) => r.data);

// Director event state (manual phases)
export const getEventState = () => api.get("/event/state").then((r) => r.data);
export const setEventState = (phase) => api.post("/admin/event/state", { phase }).then((r) => r.data);

// Phone / WhatsApp poster send tracking
export const setGuestPhone = (id, phone) =>
  api.patch(`/admin/guests/${id}/phone`, { phone }).then((r) => r.data);
export const markPosterSent = (id, sent = true) =>
  api.patch(`/admin/guests/${id}/mark-sent`, { sent }).then((r) => r.data);
export const getPosterData = (id) => api.get(`/poster/${id}`).then((r) => r.data);

// YouTube background tracks for Director scenes
export const listYtTracks = () => api.get("/director/youtube").then((r) => r.data);
export const addYtTrack = (scene, url) =>
  api.post("/admin/director/youtube", { scene, url }).then((r) => r.data);
export const deleteYtTrack = (id) => api.delete(`/admin/director/youtube/${id}`).then((r) => r.data);

